/**
 * The one-time API_Key_Value reveal (Feature: terraform-provider, task 7.2; Requirement 11.5;
 * Design: §Components 9).
 *
 * The `apiKeyValue` is the only plaintext credential the platform ever emits, it is emitted exactly
 * once per create or rotate, and it is unrecoverable afterwards because the record keeps a digest
 * (`hashApiKeySecret`, `identity/api-keys.ts`). So this dialog is the single moment the value exists in
 * a browser, and the rules that follow from that are the whole design:
 *
 *  - **It arrives as a prop and is stored nowhere.** The page holds it in `useState` and passes it
 *    down; this component keeps no copy of its own, writes it to no `localStorage`, no `sessionStorage`,
 *    no URL, and no `data-` attribute. Dismissing clears the page's state, and unmounting discards it
 *    because state is the only place it ever was — not because anything here erases it. That is a
 *    stronger guarantee than a cleanup handler, which would only be a second place to get it wrong, and
 *    `ApiKeysPage.test.tsx` pins it from the outside with storage spies and a post-unmount check.
 *  - **The copy control tells the truth about whether it copied.** `navigator.clipboard` is absent
 *    under a non-secure origin and in some hardened configurations, and an optional-chained call there
 *    resolves happily while copying nothing. A reader who trusts a false "Copied" and dismisses the
 *    dialog has lost the credential, so an unavailable or refused clipboard says so and selects the
 *    text instead, which is the fallback a keyboard user can finish by hand.
 *  - **The input is `readOnly` rather than `disabled`.** A disabled input cannot be selected, which
 *    would break exactly that fallback.
 *
 * It is deliberately NOT a {@link import('./ConfirmDialog').ConfirmDialog}: that component confirms an
 * action that has not happened yet, and this one reports one that already has. There is nothing to
 * cancel and no destructive button to hold back.
 */

import { useRef, useState } from 'react';

/** The value one create or rotate produced, held by the page for as long as the dialog is open. */
export interface RevealedApiKey {
  /** The key's name, so the dialog says which credential this is. */
  readonly name: string;
  /** The plaintext API_Key_Value. Never persisted — see the module doc. */
  readonly value: string;
  /** Which call produced it; only the heading and the lead sentence differ. */
  readonly reason: 'created' | 'rotated';
}

/** Whether the last copy attempt worked, and if not, why not. */
type CopyState = 'idle' | 'copied' | 'unavailable' | 'failed';

const COPY_MESSAGES: Record<Exclude<CopyState, 'idle'>, string> = {
  copied: 'Copied to the clipboard.',
  unavailable: 'This browser will not give the page clipboard access. The value is selected — copy it manually.',
  failed: 'The copy was refused. The value is selected — copy it manually.',
};

export interface ApiKeyRevealDialogProps {
  revealed: RevealedApiKey;
  onDismiss: () => void;
}

export function ApiKeyRevealDialog({ revealed, onDismiss }: ApiKeyRevealDialogProps): JSX.Element {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const valueInput = useRef<HTMLInputElement>(null);

  function selectValue(): void {
    valueInput.current?.select();
  }

  async function copyValue(): Promise<void> {
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (clipboard === undefined) {
      setCopyState('unavailable');
      selectValue();
      return;
    }
    try {
      await clipboard.writeText(revealed.value);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
      selectValue();
    }
  }

  const title = revealed.reason === 'created' ? 'Your new API key' : 'Your rotated API key';

  return (
    <div className="dialog-backdrop">
      <div role="dialog" aria-modal="true" aria-label={title} className="dialog card">
        <h2>{title}</h2>
        <p>
          {revealed.reason === 'created'
            ? `${revealed.name} is ready to use.`
            : `${revealed.name} has a new value, and the previous one no longer authenticates.`}
        </p>

        <div className="api-key-reveal">
          <input
            ref={valueInput}
            type="text"
            readOnly
            aria-label="API key value"
            data-testid="api-key-value"
            value={revealed.value}
            // Selecting on focus so a click or a Tab lands ready to copy, which is the whole
            // interaction this dialog exists for.
            onFocus={selectValue}
          />
          <button type="button" onClick={() => void copyValue()}>
            Copy
          </button>
        </div>

        {/* Req 11.5, verbatim: the statement that the value cannot be shown again, beside the value. */}
        <p>
          <strong>
            Copy it now. This value cannot be shown again; rotate the key to get a new one.
          </strong>
        </p>
        <p className="muted">
          Give it to a pipeline through a secret, not a file — the Terraform provider reads it from the{' '}
          <code>SOROUSH_API_KEY</code> environment variable.
        </p>

        {copyState === 'idle' ? null : (
          <p role="status" className="muted" data-testid="copy-status">
            {COPY_MESSAGES[copyState]}
          </p>
        )}

        <div className="form-actions">
          <button type="button" className="secondary" onClick={onDismiss}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
